import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, ChevronDown, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";

const NavLink = ({ to, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`relative text-sm font-medium transition-colors duration-200 ${
      active ? "text-brand-orange" : "text-brand-dark hover:text-brand-orange"
    }`}
  >
    {label}
    <span
      className={`absolute -bottom-1.5 left-0 h-0.5 bg-brand-orange transition-all duration-300 ${
        active ? "w-full" : "w-0"
      }`}
    />
  </Link>
);

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isServicesActive = pathname.startsWith("/services");

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-brand-cream shadow-sm" : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <nav className="container-x flex items-center justify-between h-[72px]">
        <Logo variant="light" />

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-9">
          <NavLink to="/" label="Home" active={pathname === "/"} />

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-trigger"
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                isServicesActive ? "text-brand-orange" : "text-brand-dark hover:text-brand-orange"
              }`}
            >
              Services
              <ChevronDown size={15} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <div
              className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[320px] transition-all duration-200 ${
                servicesOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-2"
              }`}
            >
              <div className="rounded-2xl bg-white border border-brand-cream shadow-xl p-2.5">
                {SERVICES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-dropdown-${s.slug}`}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-brand-creamSoft transition-colors group/item"
                    >
                      <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-cream text-brand-orange group-hover/item:bg-brand-orange group-hover/item:text-white transition-colors">
                        <Icon size={17} strokeWidth={1.75} />
                      </span>
                      <span className="text-sm font-medium text-brand-dark">{s.grid}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          <NavLink to="/contact" label="Contact" active={pathname === "/contact"} />
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href={SITE.phoneHref}
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-brand-dark hover:text-brand-orange transition-colors"
          >
            <Phone size={16} className="text-brand-orange" />
            {SITE.phoneDisplay}
          </a>
          <Link to="/contact" data-testid="nav-cta-quote" className="btn-cta">
            Get Free Quote
          </Link>
        </div>

        {/* Mobile trigger */}
        <div className="flex lg:hidden items-center gap-2">
          <a href={SITE.phoneHref} data-testid="nav-phone-mobile" className="grid place-items-center h-10 w-10 rounded-full bg-brand-cream text-brand-orange">
            <Phone size={18} />
          </a>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-menu-btn" aria-label="Open menu" className="grid place-items-center h-10 w-10 rounded-full bg-brand-dark text-white">
                <Menu size={19} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:w-[400px] bg-white p-0 border-l border-brand-cream">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex items-center justify-between px-6 h-[72px] border-b border-brand-cream">
                <Logo variant="light" className="h-9" />
              </div>
              <div className="flex flex-col px-6 py-6 gap-1 overflow-y-auto h-[calc(100vh-72px)]">
                <Link to="/" data-testid="mobile-nav-home" onClick={() => setMobileOpen(false)} className="py-3 text-lg font-medium text-brand-dark border-b border-brand-cream">Home</Link>
                <p className="pt-5 pb-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-orange">Our Services</p>
                {SERVICES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`mobile-nav-${s.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 py-3 text-brand-dark"
                    >
                      <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-cream text-brand-orange">
                        <Icon size={17} strokeWidth={1.75} />
                      </span>
                      <span className="text-base font-medium">{s.grid}</span>
                    </Link>
                  );
                })}
                <Link to="/contact" data-testid="mobile-nav-contact" onClick={() => setMobileOpen(false)} className="py-3 mt-3 text-lg font-medium text-brand-dark border-t border-b border-brand-cream">Contact</Link>
                <Link to="/contact" data-testid="mobile-nav-cta" onClick={() => setMobileOpen(false)} className="btn-cta w-full mt-6">Get Your Free Quote</Link>
                <a href={SITE.phoneHref} className="btn-ghost-dark w-full mt-3">
                  <Phone size={16} /> {SITE.phoneDisplay}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
